// BlastHeroSeat.jsx
//
// Blast-owned hero seat — forked from the shared
// components/tables/shared/HeroSeat/HeroSeat.jsx. The original's
// shouldShowBet/displayBet logic branches on gameConfig.betType
// ('OPEN' / 'FACING_3BET_4BET' / etc.) to override bet sizing for cash-game
// scenarios; Blast never sets betType (it passes betType={undefined} from
// BlastTable today), so all of that always fell through to the plain
// `bet > 0` case anyway. Stripping it out changes nothing about Blast's
// behavior — it just removes a class of bugs where a future cash-game-only
// change to the shared component could silently affect Blast, or vice versa.
//
// Same class names as the original (hero-seat / cards-container / card /
// bet-container / dealer-button) so hero-seat.css applies unchanged —
// visual output is identical to today's HeroSeat.
import React from 'react';
import '../../shared/HeroSeat/hero-seat.css';

const SUIT_MAP = { h: 'hearts', d: 'diamonds', c: 'clubs', s: 'spades' };

const SUIT_COLORS = {
  hearts: '#E57373',
  clubs: '#49AB78', // matches clubs.svg fill + Call action color
  diamonds: '#497CAB',
  spades: '#424242',
};

const BlastHeroSeat = ({
  blinds,
  isDealer,
  bet = 0,
  card1Value,
  card1Suit,
  card2Value,
  card2Suit,
  potSize = '0',
  showPotWon = false,
  onPotWonChange = () => {},
  title,
  // Defaults to true so anything not yet passing this prop keeps the
  // original always-gold-border look — this toggle is new prototype
  // behavior (see /blast-test), not yet in the shared HeroSeat/hero-seat.css.
  isActive = true,
  className = '',
  // True once bet represents a raise rather than the posted blind — see
  // BlastSeat.jsx for the same flag. Here the existing DOM order (chip,
  // then text) already puts text as the LAST child, which .bet-container's
  // column-reverse keeps nearest the anchored top edge — so text was
  // already stable when the chip's size changes; no reordering needed,
  // just the asset swap.
  isRaise = false,
}) => {
  const initialBlinds = parseFloat(blinds);
  const shouldShowBet = bet > 0;
  const remainingBlinds = shouldShowBet ? (initialBlinds - bet).toFixed(1) : blinds;

  const fullCard1Suit = SUIT_MAP[card1Suit] || card1Suit;
  const fullCard2Suit = SUIT_MAP[card2Suit] || card2Suit;

  React.useEffect(() => {
    onPotWonChange(showPotWon);
  }, [showPotWon, onPotWonChange]);

  return (
    <div className={`hero-seat ${isActive ? '' : 'hero-seat-inactive'} ${className}`}>
      <div className="hero-title">{title}</div>
      <div className="hero-big-blinds">{remainingBlinds}</div>

      <div className="cards-container">
        <div className="card card-1">
          <img
            src={`/svgs/${fullCard1Suit}.svg`}
            alt={fullCard1Suit}
            className="card-suit"
            style={{ color: SUIT_COLORS[fullCard1Suit] }}
          />
          <span className="card-value" style={{ color: SUIT_COLORS[fullCard1Suit] }}>
            {card1Value}
          </span>
        </div>
        <div className="card card-2">
          <img
            src={`/svgs/${fullCard2Suit}.svg`}
            alt={fullCard2Suit}
            className="card-suit"
            style={{ color: SUIT_COLORS[fullCard2Suit] }}
          />
          <span className="card-value" style={{ color: SUIT_COLORS[fullCard2Suit] }}>
            {card2Value}
          </span>
        </div>
      </div>

      {isDealer && (
        <div className="dealer-button">
          <img src="/svgs/dealer.svg" alt="Dealer" width="24" height="24" />
        </div>
      )}

      {shouldShowBet && (
        // No blind/raise class split needed here anymore — bottom-anchoring
        // (blast-animation-test.css) holds the same cards-to-chip gap for
        // both chip sizes with a single value.
        <div className="bet-container">
          {/* hero-seat.css hardcodes `.bet-chip { width: 24px; height: 24px; }`,
              which overrides plain width/height HTML attributes — same fix as
              BlastSeat.jsx, use inline style so the bigger size actually applies. */}
          <img
            src={isRaise ? '/svgs/chips-raise.svg' : '/svgs/chip.svg'}
            alt="Betting chip"
            className="bet-chip"
            style={isRaise ? { width: 32, height: 30 } : { width: 24, height: 24 }}
          />
          <span className="bet-amount">
            {bet}<span className="bet-unit">bb</span>
          </span>
        </div>
      )}

      {showPotWon && (
        <div className="pot-won-container">
          <img src="/svgs/pot-won.svg" alt="Pot Won Icon" className="pot-won-icon" width="57" height="42" />
          <span className="pot-won-amount">
            <span className="pot-won-value">{potSize}</span>
            <span className="pot-won-unit">bb</span>
          </span>
        </div>
      )}
    </div>
  );
};

export default BlastHeroSeat;
